<template>
  <div class="overflow-auto h-[70vh]">
    <v-data-table
      :headers="headers"
      :items="items"
      class="table-radius border border-[#e0e0e0]"
      show-select
      disable-sort
    >
      <template v-for="(header, index) in headers" v-slot:[`header.${header.value}`]>
        <p :key="index" class="font-title" v-tooltip="{ text: header.text }">
          {{ header.text }}
        </p>
      </template>

      <template v-slot:[`item.type`]="{ item }">
        <td class="px-4 py-3">{{ item.type }}</td>
      </template>

      <template v-slot:[`item.monthlyAllowedLimit`]="{ item }">
        <td class="px-4 py-3">{{ item.monthlyAllowedLimit }}</td>
      </template>

      <template v-slot:[`item.branchName`]="{ item }">
        <td class="px-4 py-3">{{ item.branchName }}</td>
      </template>

      <template v-slot:[`item.departmentName`]="{ item }">
        <td class="px-4 py-3">{{ item.departmentName }}</td>
      </template>

      <template v-slot:[`item.designationName`]="{ item }">
        <td class="px-4 py-3">{{ item.designationName }}</td>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <td class="px-4 py-3">
          <ActionButton
            :item="item"
            :can-edit="hasIamPermission(iamStatements, 'organization.salary_template.update')"
            :can-delete="hasIamPermission(iamStatements, 'organization.salary_template.delete')"
            :show-edit-button="hasIamPermission(iamStatements, 'organization.salary_template.update')"
            :show-delete-button="hasIamPermission(iamStatements, 'organization.salary_template.delete')"
            :handle-edit="editItem"
            :handle-delete="deleteItem"
          />
        </td>
      </template>
    </v-data-table>

    <v-dialog v-model="drawer" max-width="600px" persistent>
      <v-card>
        <ActitiveTabcomponent
          :tabs="reimTabs"
          defaultTab="reimbursementForm"
          insideDialog
        >
          <template #tab-buttons>
            <v-btn icon @click="closeDrawer">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>

          <template #reimbursementForm>
            <div class="flex flex-col h-auto bg-white">
              <!-- Scrollable Content -->
              <div class="p-4">
            <v-form ref="form" v-model="valid">
              <div class="grid grid-cols-1">
                <v-text-field
                  v-model="editedItem.type"
                  label="Type"
                  outlined
                  dense
                  :rules="[(v) => !!v || 'Type is required']"
                  required
                ></v-text-field>

                <v-text-field
                  v-model.number="editedItem.monthlyAllowedLimit"
                  label="Monthly Allowed Limit"
                  type="number"
                  outlined
                  dense
                  :rules="[(v) => !!v || 'Limit is required', (v) => v > 0 || 'Must be positive']"
                  required
                ></v-text-field>

                <v-autocomplete
                  v-model="editedItem.branch"
                  :items="branches"
                  label="Branch"
                  item-text="branchName"
                  item-value="_id"
                  outlined
                  dense
                  clearable
                ></v-autocomplete>

                <v-autocomplete
                  v-model="editedItem.department"
                  :items="departments"
                  label="Department"
                  item-text="departmentName"
                  item-value="_id"
                  outlined
                  dense
                  clearable
                  @change="fetchDesignations(editedItem.department)"
                ></v-autocomplete>

                <v-autocomplete
                  v-model="editedItem.designation"
                  :items="designations"
                  label="Designation"
                  item-text="designationName"
                  item-value="_id"
                  outlined
                  dense
                  clearable
                  :disabled="!editedItem.department"
                ></v-autocomplete>
              </div>
            </v-form>
              </div>

              <!-- Fixed Footer -->
              <div>
                <v-divider></v-divider>
                <div class="flex justify-end p-4">
                  <v-btn class="normal-case mr-2" text @click="closeDrawer">Cancel</v-btn>
                  <v-btn
                    color="primary"
                    class="normal-case"
                    v-iam-disabled="'organization.salary_template.update'"
                    @click="save"
                    :disabled="!valid"
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    Save
                  </v-btn>
                </div>
              </div>
            </div>
          </template>
        </ActitiveTabcomponent>
      </v-card>
    </v-dialog>

    <AlertPopUp
      :showConfirmationDialog="showConfirmationDialog"
      :confirmationTitle="'Confirm Delete'"
      :confirmationMessage="'Are you sure you want to delete this reimbursement template?'"
      :confirmationActionText="'Delete'"
      :confirmationActionColor="'red'"
      :performAction="confirmDelete"
      :cancel="cancelDelete"
    />
  </div>
</template>

<script>
import { axiosUrl } from '@/utils/workforceApi'
import { hasIamPermission } from '@/utils/iamEvaluator'
import ActionButton from '@/components/ActionButton.vue'
import AlertPopUp from '@/components/AlertPopUp'
import ActitiveTabcomponent from '@/components/ActitiveTabcomponent.vue'

export default {
  name: 'ReimbursementTable',
  components: {
    ActionButton,
    AlertPopUp,
    ActitiveTabcomponent
  },
  data: () => ({
    drawer: false,
    valid: false,
    headers: [
      { text: 'Type', value: 'type' },
      { text: 'Monthly Limit', value: 'monthlyAllowedLimit' },
      { text: 'Branch', value: 'branchName' },
      { text: 'Department', value: 'departmentName' },
      { text: 'Designation', value: 'designationName' },
      { text: 'Actions', value: 'actions', sortable: false }
    ],
    items: [],
    branches: [],
    departments: [],
    designations: [],
    allDesignations: [],
    editedIndex: -1,
    editedItem: {
      type: '',
      monthlyAllowedLimit: 0,
      branch: null,
      department: null,
      designation: null
    },
    defaultItem: {
      type: '',
      monthlyAllowedLimit: 0,
      branch: null,
      department: null,
      designation: null
    },
    showConfirmationDialog: false,
    itemToDelete: null
  }),

  computed: {
    reimTabs() {
      return [
        {
          key: 'reimbursementForm',
          label: this.editedIndex === -1 ? 'New Reimbursement' : 'Edit Reimbursement',
          icon: this.editedIndex === -1 ? 'mdi-plus' : 'mdi-pencil',
          component: 'Reimbursements'
        }
      ]
    },
    iamStatements() {
      return this.$store.state.wfmStatements || []
    }
  },

  async created() {
    await Promise.all([this.fetchBranches(), this.fetchDepartments(), this.fetchAllDesignations()])
    this.fetchReimbursements()
  },

  methods: {
    hasIamPermission,
    async fetchReimbursements() {
      try {
        const response = await this.$axios.get('/orgSetup/custom-reimbursement')
        this.items = (response.data.customReimbursements || []).map((item) => {
          const branch = this.branches.find((b) => b._id === item.branch)
          const department = this.departments.find((d) => d._id === item.department)
          const designation = this.allDesignations.find((d) => d._id === item.designation)

          return {
            ...item,
            branchName: branch ? branch.branchName : '-',
            departmentName: department ? department.departmentName : '-',
            designationName: designation ? designation.designationName : '-'
          }
        })
      } catch (error) {
        console.error('Error fetching reimbursements:', error)
      }
    },

    async fetchBranches() {
      try {
        const response = await this.$axios.get(axiosUrl.branches.getBranches)
        this.branches = response.data.branches || []
      } catch (error) {
        console.error('Error fetching branches:', error)
      }
    },

    async fetchDepartments() {
      try {
        const response = await this.$axios.get(axiosUrl.departments.getDepartments)
        this.departments = response.data.departments || []
      } catch (error) {
        console.error('Error fetching departments:', error)
      }
    },

    async fetchAllDesignations() {
      try {
        const response = await this.$axios.get(axiosUrl.designations.getDesignations)
        this.allDesignations = response.data.designations || []
      } catch (error) {
        console.error('Error fetching all designations:', error)
      }
    },

    async fetchDesignations(departmentId) {
      if (!departmentId) {
        this.designations = []
        return
      }
      try {
        const url = `${axiosUrl.designations.getDesignations}?department=${departmentId}`
        const response = await this.$axios.get(url)
        this.designations = response.data.designations || []
      } catch (error) {
        console.error('Error fetching designations:', error)
      }
    },

    openCreateDialog() {
      this.editedIndex = -1
      this.editedItem = Object.assign({}, this.defaultItem)
      this.drawer = true
      this.$nextTick(() => {
        this.$refs.form.resetValidation()
      })
    },

    closeDrawer() {
      this.drawer = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
    },

    editItem(item) {
      this.editedIndex = this.items.indexOf(item)
      this.editedItem = Object.assign({}, item)
      // Ensure IDs are set correctly for autocomplete
      this.editedItem.branch = item.branch || null
      this.editedItem.department = item.department || null
      this.editedItem.designation = item.designation || null

      if (this.editedItem.department) {
        this.fetchDesignations(this.editedItem.department)
      }

      this.drawer = true
    },

    deleteItem(item) {
      this.itemToDelete = item
      this.showConfirmationDialog = true
    },

    async confirmDelete() {
      if (!this.itemToDelete) return

      try {
        await this.$axios.delete(`/orgSetup/custom-reimbursement/${this.itemToDelete._id}`)
        this.$toast.success('Reimbursement deleted successfully')
        this.fetchReimbursements()
      } catch (error) {
        console.error('Error deleting reimbursement:', error)
        this.$toast.error('Failed to delete reimbursement')
      } finally {
        this.cancelDelete()
      }
    },

    cancelDelete() {
      this.showConfirmationDialog = false
      this.itemToDelete = null
    },

    async save() {
      if (!this.$refs.form.validate()) return

      try {
        const payload = {
          ...this.editedItem
        }

        // Remove null/undefined values for optional fields
        if (!payload.branch) delete payload.branch
        if (!payload.department) delete payload.department
        if (!payload.designation) delete payload.designation

        // Remove UI specific fields
        delete payload.branchName
        delete payload.departmentName
        delete payload.designationName

        if (this.editedIndex > -1) {
          await this.$axios.put(`/orgSetup/custom-reimbursement/${this.editedItem._id}`, payload)
        } else {
          await this.$axios.post('/orgSetup/custom-reimbursement', payload)
        }

        this.$toast.success('Reimbursement saved successfully')
        this.closeDrawer()
        this.fetchReimbursements()
      } catch (error) {
        console.error('Error saving reimbursement:', error)
        this.$toast.error('Failed to save reimbursement')
      }
    }
  }
}
</script>

<style scoped>
.form-text {
  font-size: 1.25rem;
  font-weight: 600;
  color: #21427d;
  margin: 0;
}
.table-radius {
  border-radius: 0px !important;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.v-data-table >>> .v-data-table__wrapper > table > tbody > tr > td > .v-data-table__checkbox {
  text-align: center;
}
</style>
